import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  NotificationBanner,
  PrimaryButton,
} from "@bloomberg/media-ui-core";
import { Refresh } from "@bloomberg/media-ui-icons";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useRegUIClient } from "@foundation/reg-ui-client";
import type {
  GetUserWatchlistsResponse,
  Watchlist,
} from "@foundation/watchlist-client";
import styles from "./styles.module.css";
import type { PortfolioForm } from "~types/portfolio";
import {
  createPortfolio,
  fetchPortfolioById,
  updatePortfolioById,
} from "~api/portfolio/portfolioData";
import {
  FORM_ACTION,
  WATCHLIST_FAILURE_TEXT,
  DEFAULT_PORTFOLIO_FORM,
  LOADING_STATE,
  WATCHLIST_SAVE_FAILURE_TEXT,
} from "~constants/Portfolio";
import {
  prepareCreatePortfolioData,
  prepareFormData,
  prepareUpdatePortfolioData,
} from "~utils/portfolio/hydratePortfolioData";
import PortfolioLoadingState from "~components/Portfolio/PortfolioLoadingState";
import Notification from "~components/Notification";
import PortfolioHeader from "~components/Portfolio/PortfolioForm/PortfolioHeader";
import PortfolioHoldings from "~components/Portfolio/PortfolioForm/PortfolioHoldings";
import FormHeader from "~components/Portfolio/PortfolioForm/FormHeader";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { usePortfolioFormValidation } from "~context/PortfolioFormValidationContext";

const savePortfolio = async (formData: PortfolioForm, bconnectId: string) => {
  try {
    // update existing portfolio
    if (formData.id !== undefined && formData.id > 0) {
      const portfolioData = prepareUpdatePortfolioData(formData);

      return await updatePortfolioById(
        bconnectId,
        formData.id,
        portfolioData
      ).then((response) => (response.data as GetUserWatchlistsResponse).port);
    }

    // create new portfolio
    const newPortfolioData = prepareCreatePortfolioData(formData);

    return await createPortfolio(bconnectId, newPortfolioData).then(
      (response) => (response.data as GetUserWatchlistsResponse).port
    );
  } catch (error) {
    throw error;
  }
};

type FormFooterProps = {
  onSaveClick: () => void;
  loading: boolean;
};

function FormFooter(props: FormFooterProps) {
  const { onSaveClick, loading } = props;

  return (
    <div className={styles.footer}>
      <PrimaryButton
        className={styles.button}
        type="button"
        bold={false}
        fullWidth={true}
        loading={loading}
        onClick={onSaveClick}
        title="Save Changes"
      >
        <span className={styles.buttonText}>Save Changes</span>
      </PrimaryButton>
    </div>
  );
}

type FormProps = {
  portfolioId: number;
  onFormEvent: (action: string) => Promise<void>;
};

export default function PortfolioForm(props: FormProps) {
  const { portfolioId, onFormEvent } = props;
  const [isValidForm, setIsValidForm] = useState(true);
  const [loadingState, setLoadingState] = useState(LOADING_STATE.LOADING);
  const [savingState, setSavingState] = useState(LOADING_STATE.EMPTY);
  const [formData, setFormData] = useState<PortfolioForm>(
    DEFAULT_PORTFOLIO_FORM
  );

  const { resetValidationState, isFormValid } = usePortfolioFormValidation();

  const apiCalled = useRef(false);
  const {
    selectedPortInfo,
    portfolioForm,
    updatePortfolioForm,
    upsertPortfolio,
  } = usePortfolioPage();
  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const refreshPortfolio = () => {
    if (portfolioId === -1) {
      // update page context to reset form data
      updatePortfolioForm(DEFAULT_PORTFOLIO_FORM);
      setLoadingState(LOADING_STATE.COMPLETE);

      return;
    }

    setLoadingState(LOADING_STATE.LOADING);

    fetchPortfolioById(bconnectId, portfolioId)
      .then((result) => {
        const fetchedPortfolio = (result.data as GetUserWatchlistsResponse)
          .port;

        // update page context
        updatePortfolioForm(prepareFormData(fetchedPortfolio));

        // update local state
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((error) => {
        console.error(error);
        // handle client error
        setLoadingState(LOADING_STATE.FAILED);
      });
  };

  // expected to run once per render or when portfolio id changes
  useEffect(() => {
    if (apiCalled.current) {
      return;
    }

    apiCalled.current = true;

    // clear context validation state on render
    resetValidationState();

    // fetch latest portfolio data
    refreshPortfolio();
  }, [portfolioId]);

  // to sync context data with local copy for save operation
  useEffect(() => {
    if (!portfolioForm) {
      return;
    }

    setFormData(portfolioForm);
  }, [portfolioForm]);

  if (loadingState === LOADING_STATE.LOADING) {
    return <PortfolioLoadingState />;
  } else if (loadingState === LOADING_STATE.FAILED) {
    return (
      <div className={styles.loadingFailed}>
        <Notification
          text={WATCHLIST_FAILURE_TEXT}
          variant="lineBreak"
          title="Error"
          actionIcon={<Refresh />}
          actionText="Refresh"
          onActionClick={refreshPortfolio}
        />
      </div>
    );
  }

  const handleCancelClick = () => {
    onFormEvent(FORM_ACTION.CANCEL).catch(console.error);
  };

  const handleDeleteClick = () => {
    onFormEvent(FORM_ACTION.DELETE).catch(console.error);
  };

  const handleSaveSuccessResponse = (updatedPortfolio: Watchlist) => {
    setSavingState(LOADING_STATE.COMPLETE);

    // handle adding new portfolio and updating existing
    upsertPortfolio(updatedPortfolio);

    // close modal
    handleCancelClick();
  };

  const handleSaveClick = () => {
    // validate form entry
    if (!isFormValid()) {
      setIsValidForm(false);

      return;
    }

    setIsValidForm(true);

    // track if portfolio was renamed
    if (selectedPortInfo?.name !== formData?.name)
      trackClickEvent(
        "watchlistRenamed",
        "renamed_watchlist",
        "success",
        "watchlist"
      );

    setSavingState(LOADING_STATE.LOADING);

    // format data and call save api
    savePortfolio(formData, bconnectId)
      .then(handleSaveSuccessResponse)
      .catch(() => {
        // handle client error
        setSavingState(LOADING_STATE.FAILED);
      });
  };

  const newPortfolio = portfolioId < 0;

  return (
    <Form
      className={styles.portfolioForm}
      onSubmit={() => {
        return false;
      }}
    >
      <FormHeader
        title={newPortfolio ? "Create Watchlist" : "Edit Watchlist"}
        showDelete={!newPortfolio}
        onCancelClick={handleCancelClick}
        onDeleteClick={handleDeleteClick}
      />
      <div className={styles.body}>
        {!isValidForm && (
          <NotificationBanner
            className="mb-2"
            title="Please correct the highlighted fields before saving your changes."
            variant="error"
          />
        )}

        {savingState === LOADING_STATE.FAILED && (
          <Notification
            text={WATCHLIST_SAVE_FAILURE_TEXT}
            variant="default"
            title=""
          />
        )}

        <PortfolioHeader />
        <PortfolioHoldings />
      </div>
      <FormFooter
        onSaveClick={handleSaveClick}
        loading={savingState === LOADING_STATE.LOADING}
      />
    </Form>
  );
}
